import { useRef, ReactNode, MouseEvent } from "react";
import { gsap } from "gsap";

interface TiltGlassCardProps {
  children: ReactNode;
  className?: string;
  /** Max tilt angle in degrees */
  max?: number;
  /** Glare intensity 0–1 */
  glare?: number;
}

/**
 * Floating glassmorphism card with dynamic mouse-tracked tilt.
 * Pure GSAP — no extra deps. Auto-resets on leave.
 */
export default function TiltGlassCard({
  children,
  className = "",
  max = 8,
  glare = 0.35,
}: TiltGlassCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const glareRef = useRef<HTMLDivElement>(null);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rx = (0.5 - py) * max * 2;
    const ry = (px - 0.5) * max * 2;

    gsap.to(el, {
      rotateX: rx,
      rotateY: ry,
      transformPerspective: 1000,
      transformOrigin: "center",
      duration: 0.5,
      ease: "power3.out",
    });

    if (glareRef.current) {
      gsap.to(glareRef.current, {
        opacity: glare,
        background: `radial-gradient(circle at ${px * 100}% ${py * 100}%, hsl(0 0% 100% / 0.5), transparent 60%)`,
        duration: 0.4,
        ease: "power2.out",
      });
    }
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    gsap.to(el, {
      rotateX: 0,
      rotateY: 0,
      duration: 0.8,
      ease: "elastic.out(1, 0.5)",
    });
    if (glareRef.current) {
      gsap.to(glareRef.current, { opacity: 0, duration: 0.4 });
    }
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`relative will-change-transform ${className}`}
      style={{ transformStyle: "preserve-3d" }}
    >
      {children}
      <div
        ref={glareRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 mix-blend-soft-light"
      />
    </div>
  );
}
